import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '@/auth/useAuth';
import { KaiLogo } from '@/components/branding/KaiLogo';
import { Button } from '@/components/ui/Button';
import { IconButton } from '@/components/ui/IconButton';
import {
  MicrosoftIcon,
  MoonIcon,
  ShieldCheckIcon,
  SunIcon,
} from '@/components/ui/Icon';
import { appConfig, isAuthConfigured } from '@/config/appConfig';
import { ROUTES } from '@/routes/paths';
import { useTheme } from '@/theme/useTheme';

import { LegalDocModal } from './LegalDocModal';
import { type LegalDocId } from './legalContent';
import styles from './LoginPage.module.css';

interface LocationState {
  from?: { pathname?: string };
}

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggle } = useTheme();
  const { isAuthenticated, isInteractionInProgress, signIn } = useAuth();
  const [error, setError] = useState<string | null>(null);
  // Which legal document modal is open (PLA-1091); null = none.
  const [openLegalDoc, setOpenLegalDoc] = useState<LegalDocId | null>(null);

  const redirectTarget =
    (location.state as LocationState | null)?.from?.pathname ?? ROUTES.dashboard;

  useEffect(() => {
    if (isAuthenticated) navigate(redirectTarget, { replace: true });
  }, [isAuthenticated, navigate, redirectTarget]);

  const configured = isAuthConfigured();
  const isDark = theme === 'dark';

  const handleSignIn = async () => {
    setError(null);
    try {
      await signIn();
    } catch (e) {
      console.error('[login] sign-in failed', e);
      setError(
        e instanceof Error ? e.message : 'Unable to start sign-in. Please try again.',
      );
    }
  };

  return (
    <div className={styles.root}>
      <aside className={styles.brandPanel}>
        <span className={styles.aurora} aria-hidden="true" />
        <span className={styles.grid} aria-hidden="true" />

        <header className={styles.brandHeader}>
          <span className={styles.statusBadge}>
            <span className={styles.statusDot} aria-hidden="true" />
            <span>Live</span>
          </span>
        </header>

        <div className={styles.brandCenter}>
          <span className={styles.markWrap}>
            <span className={styles.markGlow} aria-hidden="true" />
            <KaiLogo size={92} variant="default" className={styles.brandMark} />
          </span>

          <h1 className={styles.brandTitle}>{appConfig.appName}</h1>
          <p className={styles.brandTagline}>
            A calm, signed-in workspace for the work that matters.
          </p>
        </div>

        <footer className={styles.brandFooter}>
          <span className={styles.brandFooterRule} aria-hidden="true" />
          <span className={styles.brandFooterText}>
            Organization {maskTenantId(appConfig.entraTenantId)}
          </span>
        </footer>
      </aside>

      <section className={styles.formPanel}>
        <div className={styles.toggleHolder}>
          <IconButton
            aria-label={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
            onClick={toggle}
            title={isDark ? 'Light mode' : 'Dark mode'}
          >
            {isDark ? <SunIcon /> : <MoonIcon />}
          </IconButton>
        </div>

        <div className={styles.formInner}>
          <div className={styles.form}>
            <div className={styles.compactBrand}>
              <KaiLogo size={40} label={appConfig.appName} />
            </div>

            <header className={styles.formHeader}>
              <h2 className={styles.formTitle}>Welcome back.</h2>
              <p className={styles.formSubtitle}>
                Sign in with your organization account to continue.
              </p>
            </header>

            {!configured && (
              <div className={styles.alert} role="alert" data-variant="warning">
                <strong>Entra ID is not configured.</strong>
                <span>
                  Set <code>VITE_KAI_APP_CLIENT_ID</code> and{' '}
                  <code>VITE_KAI_TENANT_ID</code> in your <code>.env</code> file, or{' '}
                  <code>KAI_APP_CLIENT_ID</code> and <code>KAI_TENANT_ID</code> in the
                  container environment.
                </span>
              </div>
            )}

            {error && (
              <div className={styles.alert} role="alert" data-variant="error">
                {error}
              </div>
            )}

            <div className={styles.cta}>
              <span className={styles.ctaAccent} aria-hidden="true" />
              <Button
                size="lg"
                fullWidth
                onClick={handleSignIn}
                loading={isInteractionInProgress}
                disabled={!configured}
                leadingIcon={<MicrosoftIcon width={18} height={18} />}
              >
                Continue with Microsoft
              </Button>
            </div>

            <p className={styles.security}>
              <ShieldCheckIcon width={14} height={14} />
              <span>Secured by Microsoft Entra ID · Single sign&#8209;on</span>
            </p>
          </div>
        </div>

        <footer className={styles.formFooter}>
          <p className={styles.legalLine}>
            By continuing, you agree to our{' '}
            <button
              type="button"
              className={styles.legalLink}
              onClick={() => setOpenLegalDoc('terms')}
            >
              End User License Agreement
            </button>{' '}
            and acknowledge our{' '}
            <button
              type="button"
              className={styles.legalLink}
              onClick={() => setOpenLegalDoc('privacy')}
            >
              Privacy Policy
            </button>{' '}
            and{' '}
            <button
              type="button"
              className={styles.legalLink}
              onClick={() => setOpenLegalDoc('ai')}
            >
              AI Governance Policy
            </button>
            .
          </p>
        </footer>
      </section>

      <LegalDocModal docId={openLegalDoc} onClose={() => setOpenLegalDoc(null)} />
    </div>
  );
}

const maskTenantId = (id: string): string => {
  if (!id) return '—';
  if (id.length <= 8) return id;
  return `${id.slice(0, 4)}…${id.slice(-4)}`;
};
